import FormWithValidation from "./form-w-validation";
import countryList from "@/data/countries.json";
import { useEffect, useRef, useState } from "react";
import PhoneNoInput from "../phone-no-input";
import Alert from "../alert";
import states from "@/data/states.json";
import classNames from "classnames";
import { RecipientCountryOptions } from "@/components/forms/recipient-country-options";
import { RURAL_SURCHARGE_SGD } from "../../lib/constants";

export const getCountryCallingCode = (country) => {
  if (country) {
    const countryCallingCodeObj = countryList.find(
      (countryCallCodeObj) => countryCallCodeObj.isoCode === country
    );
    return countryCallingCodeObj.isoCode + "/" + countryCallingCodeObj.dialCode;
  }
};

export default function RecipientDetails({
  formData,
  setFormData,
  isRural,
  recipientValidationStates,
  COUNTRIES_WITH_STATES,
  updateFormData,
  savedRecipients,
}) {
  const [
    { state: validRecipientName, setState: setValidRecipientName },
    { state: validRecipientMobileNo, setState: setValidRecipientMobileNo },
    { state: validRecipientAddress, setState: setValidRecipientAddress },
    { state: validRecipientPostalCode, setState: setValidRecipientPostalCode },
    { state: validRecipientCity, setState: setValidRecipientCity },
    { state: validRecipientState, setState: setValidRecipientState },
    { state: validRecipientCountry, setState: setValidRecipientCountry },
  ] = recipientValidationStates;
  const [validRecipientEmail, setValidRecipientEmail] = useState("none");
  const recipientCallCodeInitialRender = useRef(true);

  //searches countryCallingCode for the country code and returns phone code

  useEffect(() => {
    if (formData.recipientCountry === "") return;
    const callCode = getCountryCallingCode(formData.recipientCountry);

    if (recipientCallCodeInitialRender.current) {
      if (formData.recipientCallCode === "") {
        updateFormData("recipientCallCode", callCode);
      }
      recipientCallCodeInitialRender.current = false;
      return;
    }

    updateFormData("recipientCallCode", callCode);
  }, [formData.recipientCountry]);

  const setFormWithSavedRecipient = (index) => {
    const query = {
      recipientName: savedRecipients[index].name,
      recipientMobileNo: savedRecipients[index].subNumber,
      recipientCallCode: savedRecipients[index].countryCallCode,
      recipientEmail: savedRecipients[index].email,
      recipientAddress:
        savedRecipients[index].address ||
        savedRecipients[index].address1 +
          (savedRecipients[index].address2
            ? `, ${savedRecipients[index].address2}`
            : ""),
      recipientPostalCode: savedRecipients[index].zip,
      recipientCity: savedRecipients[index].city,
      recipientState: savedRecipients[index].state,
      recipientCountry: savedRecipients[index].country,
    };
    setFormData({ ...formData, ...query });
  };

  return (
    <div className="bg-white w-full md:rounded-xl shadow">
      <div className="w-[86%] md:w-[50%] mx-auto pt-6 md:pt-8 pb-3">
        <FormWithValidation
          label="Recipient's Name"
          id="recipientName"
          state={formData.recipientName}
          setState={(state) => {
            updateFormData("recipientName", state);
          }}
          isValid={validRecipientName}
          setIsValid={setValidRecipientName}
          category="name"
          type="text"
          options={
            savedRecipients.length > 0 &&
            savedRecipients.map((recipient, index) => (
              <option key={index + recipient.name} value={index}>
                {recipient.name}
              </option>
            ))
          }
          selectDefault="Select saved recipient"
          selectOnChange={(e) => setFormWithSavedRecipient(e.target.value)}
          selectOnBlur={() => {
            if (recipientName) {
              setValidRecipientName(true);
            }
          }}
        />
        <FormWithValidation
          label="Country"
          id="recipientCountry"
          selectValue={formData.recipientCountry}
          selectOnChange={(event) => {
            updateFormData("recipientCountry", event.target.value);
            updateFormData("recipientState", "");
            updateFormData("validRecipientState", "none");
            setValidRecipientCountry(true);
          }}
          selectOnBlur={() => {
            if (formData.recipientCountry) {
              setValidRecipientCountry(true);
            }
          }}
          selectDefault=""
          isValid={validRecipientCountry}
          setIsValid={setValidRecipientCountry}
          options={<RecipientCountryOptions />}
        />

        <div className="mb-6">
          <PhoneNoInput
            label="Recipient's Mobile"
            callCode={formData.recipientCallCode}
            setCallCode={(callCode) => {
              updateFormData("recipientCallCode", callCode);
            }}
            name="recipientMobileNo"
            type="mobile"
            state={formData.recipientMobileNo}
            setState={(state) => {
              updateFormData("recipientMobileNo", state);
            }}
            isValid={validRecipientMobileNo}
            setIsValid={setValidRecipientMobileNo}
          />
        </div>

        <div className="mb-8">
          <FormWithValidation
            label="Recipient's Email (strongly recommended)"
            id="recipientEmail"
            category="recipientEmail"
            type="email"
            state={formData.recipientEmail}
            setState={(state) => {
              updateFormData("recipientEmail", state);
            }}
            isValid={validRecipientEmail}
            setIsValid={setValidRecipientEmail}
            guideText="Customs may email duty/ID links. No email? Use yours — leaving it blank can delay delivery."
          />
        </div>

        <FormWithValidation
          label="Address"
          id="recipientAddress"
          type="text"
          category="address"
          state={formData.recipientAddress}
          setState={(state) => {
            updateFormData("recipientAddress", state);
          }}
          isValid={validRecipientAddress}
          setIsValid={setValidRecipientAddress}
        />
        <FormWithValidation
          label="Postal Code"
          id="recipientPostalCode"
          type="text"
          category="postal"
          state={formData.recipientPostalCode}
          setState={(state) => {
            updateFormData("recipientPostalCode", state);
          }}
          isValid={validRecipientPostalCode}
          setIsValid={setValidRecipientPostalCode}
          country={formData.recipientCountry}
        />
        <div className="grid grid-cols-2 gap-4">
          <div>
            <FormWithValidation
              label="City"
              id="recipientCity"
              type="text"
              category="city"
              state={formData.recipientCity}
              setState={(state) => {
                updateFormData("recipientCity", state);
              }}
              isValid={validRecipientCity}
              setIsValid={setValidRecipientCity}
            />
          </div>
          {COUNTRIES_WITH_STATES.includes(formData.recipientCountry) ? (
            <FormWithValidation
              label="State"
              id="recipientState"
              options={states[formData.recipientCountry].map((state) => (
                <option value={state.code} key={state.code}>
                  {state.name}
                </option>
              ))}
              selectValue={formData.recipientState}
              selectOnChange={(event) => {
                updateFormData("recipientState", event.target.value);
                setValidRecipientState(true);
              }}
              selectDefault="Select State"
              isValid={validRecipientState}
              setIsValid={setValidRecipientState}
            />
          ) : (
            <div>
              <label
                htmlFor="recipientState"
                className={classNames(
                  "font-normal text-subheading-gray w-max",
                  "group-focus-within:text-dark-purple"
                )}
              >
                State
              </label>
              <input
                type="text"
                className="disabled:bg-gray-2 rounded-xl w-full h-10 mt-1"
                id="recipientState"
                disabled
                value=""
              />
            </div>
          )}
        </div>
        {isRural && (
          <Alert
            message={`Rural Surcharge of S$${RURAL_SURCHARGE_SGD.toFixed(
              2
            )} will be imposed`}
          />
        )}
      </div>
    </div>
  );
}
